import { forwardRef, useImperativeHandle } from 'react'
import { StationChartDataProps, useStationsChartData } from '../hooks/useStationsChartData'
import { Flex, FlexProps, Spinner, Strong, Text } from '@radix-ui/themes'
import { AreaChart, BarChart } from '@tremor/react'
import _ from 'lodash'
import { ChartType } from '../schemas'

import { useTranslation } from 'react-i18next'
import { GoSquareFill } from 'react-icons/go'
import { DateTime } from 'luxon'
import { formatNumber } from '~/utils/formating'
import { DateRange } from '~/types'
import { useNow } from '~/hooks/useNow'
import { Resolution } from '@willie/schemas'
import { ChartVignettes, ChartVignette } from '~/features/dashboarding/components/ChartVignette'

const COLORS = [
  'sky-400',
  'emerald-400',
  'amber-300',
  'fuchsia-500',
  'blue-600',
  'indigo-400',
  'lime-300',
  'pink-300',
  'zinc-600',
  'orange-500',
  'rose-500',
  'slate-800',
  'purple-600',
  'green-700',
  'stone-800',
  'red-400'
]

// TODO add a notice when station isn't mapped to a meter (data source)
export type StationsConsumptionChartProps = {
  title: string
  chartType: ChartType
  showLegend?: boolean
  showGridLines?: boolean
  showAnimation?: boolean
  showYAxis?: boolean
  yAxisLabel?: string
  showMeta?: boolean
  resolution?: Resolution
} & Omit<FlexProps, 'as'> &
  Omit<StationChartDataProps, 'startDate' | 'endDate'> &
  Partial<DateRange>

const StationsConsumptionChart = forwardRef((props: StationsConsumptionChartProps, ref) => {
  // Set default values for options if not provided
  const {
    showAnimation = true,
    showYAxis = true,
    showGridLines = false,
    showLegend = false,
    showMeta = true,
    resolution = 'day',
    chartType = 'area',
    title,
    stations,
    partitionKey,
    chartKey,
    partitionsOptions,
    startDate,
    endDate,
    ...flexProps
  } = props

  const { t } = useTranslation('sites')

  const now = useNow()
  const dateRange = {
    startDate: startDate || now.minus({ days: 6 }).startOf('day'),
    endDate: endDate || now.endOf('day')
  }

  const { chartData, isLoading, categories, consolidatedOverPeriod } = useStationsChartData({
    title: title,
    stations: stations,
    partitionKey: partitionKey,
    chartKey: chartKey,
    partitionsOptions: partitionsOptions,
    resolution,
    ...dateRange
  })

  useImperativeHandle(ref, () => ({
    getExportData: () => chartData
  }))

  const displayLegend = showLegend && !!props.partitionKey
  const yLabel = props.yAxisLabel || t('stations.leakSection.chart.yAxisLabel')

  if (isLoading) {
    return (
      <div>
        {props.title && (
          <Text size={'2'} color="gray">
            <Strong>{props.title}</Strong>
          </Text>
        )}
        <Flex width={'100%'} align={'center'} justify={'center'} className="h-80">
          <Spinner size={'3'} />
        </Flex>
      </div>
    )
  }

  if (!chartData) {
    return <div>{t('stations.charts.noData')}</div>
  }

  const chartProps = {
    data: chartData,
    categories: categories,
    index: 'date',
    colors: COLORS.slice(0, categories.length),
    showGridLines,
    showLegend: displayLegend,
    showAnimation,
    showYAxis,
    className: props.className,
    noDataText: t('stations.charts.noDataPeriod'),
    yAxisLabel: yLabel,
    valueFormatter: (value: number) => `${formatNumber(value)} m³`,
    yAxisWidth: 75
  }

  const chart =
    chartType === 'area' ? (
      <AreaChart {...chartProps} />
    ) : chartType === 'bar' ? (
      <BarChart {...chartProps} stack />
    ) : (
      // TODO add table chart
      <>{t('stations.charts.unsupportedChartType')}</>
    )

  const formatVignetteDate = (date: DateTime) =>
    date.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' })

  return (
    <Flex {...flexProps} direction={'column'} gap={'5'}>
      {title && (
        <Text size={'2'} color="gray">
          <Strong>{title}</Strong>
        </Text>
      )}
      {showMeta && consolidatedOverPeriod && (
        <ChartVignettes className="px-8 py-2">
          {consolidatedOverPeriod.map(({ value, label }, i) => (
            <ChartVignette
              key={i}
              icon={<GoSquareFill className={`fill-${COLORS[i]}`} size={22} />}
              title={consolidatedOverPeriod.length > 1 ? label : t('stations.leakSection.chart.consumption')}
              subtitle={`${formatVignetteDate(dateRange.startDate)} ${t('budget.chart.dateTo')} ${formatVignetteDate(dateRange.endDate)}`}
              value={
                <>
                  {formatNumber(value)} m<sup>3</sup>
                </>
              }
            />
          ))}
        </ChartVignettes>
      )}
      {chart}
    </Flex>
  )
})

export { StationsConsumptionChart }
